import { BaseIconFrame } from '@components/FramePolygonSized';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { CameraView } from '@EcamModel/model/CameraView';
import { Autorenew } from '@mui/icons-material';
import { Skeleton, Stack, Tooltip } from '@mui/material';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import color from '@theme/Colors';
import Konva from 'konva';
import moment from 'moment';
import { FC, useRef, useState } from 'react';
import { Image, Layer, Stage } from 'react-konva';
import { cameraViewBlob } from '..';
import { useCameraDetailContext } from '../../../../../hook/CameraDetailsProvider';
import StyledMenu from '../../components/StyledMenu';
import { BaseBoxDecoration, PreviewImageWithGeoZone } from '../../solar/components/HistoriesPhoto';
import GeoZonesHistoriesCameraPTZ from './components/GeoZonesHistoriesCameraPTZ';

type Props = {
    cameraView: CameraView;
    prevGeoZonesCameraPTZ: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    scaledPrevGeoZoneCameraPTZ: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    handleMouseMove: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    refreshCameraView: () => void;
    loadGeozones: boolean;
};

const HistoriesPhotoCameraPTZ: FC<Props> = (props: Props) => {
    const {
        cameraView,
        prevGeoZonesCameraPTZ,
        scaledPrevGeoZoneCameraPTZ,
        handleMouseMove,
        refreshCameraView,
        loadGeozones,
    } = props;

    const stageRef = useRef<Konva.Stage>(null);

    const {
        timeZone,
        scaleFullCameraPTZ,
        scaleNotFullCameraPTZ,

        hoveredFrame,
        anchorElMenuLists,
        listCameraView,
        setHoveredFrame,

        containerRef,
        containerSize,
    } = useCameraDetailContext();

    const { image, loading: loadingImage } = useImageDimensions(
        `${cameraView?.LastPhotoBlob}?${JSON.stringify(cameraView?.Updated)}`,
        cameraViewBlob
    );

    const [openIMGPreview, setOpenIMGPreview] = useState<boolean>(false);

    const handleClickImagePreview = () => {
        setOpenIMGPreview(true);
    };

    return (
        <>
            {loadingImage || loadGeozones ? (
                <Skeleton
                    width="100%"
                    ref={containerRef}
                    sx={{
                        borderRadius: '4px',
                        bgcolor: color.grey200,
                        minHeight: containerSize.height,
                        width: '100%',
                    }}
                    variant="rectangular"
                />
            ) : (
                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                        height: containerSize.height,
                        position: 'relative',
                    }}
                >
                    <Stage
                        ref={stageRef}
                        width={containerSize.width}
                        height={containerSize.height}
                        onMouseMove={handleMouseMove}
                        onClick={handleClickImagePreview}
                        style={{
                            cursor: 'pointer',
                        }}
                    >
                        <Layer>
                            <Image image={image!} scaleX={scaleNotFullCameraPTZ} scaleY={scaleNotFullCameraPTZ} />
                            {!!scaledPrevGeoZoneCameraPTZ.length &&
                                scaledPrevGeoZoneCameraPTZ.map((frame, frameIndex) => {
                                    return (
                                        <GeoZonesHistoriesCameraPTZ
                                            key={frameIndex}
                                            frame={frame}
                                            index={frameIndex}
                                            hoveredFrame={hoveredFrame?.index}
                                            listGeoZonesByView={cameraView.GeoZones ?? []}
                                            isCameraView={hoveredFrame?.cameraViewId === cameraView.Id}
                                        />
                                    );
                                })}
                        </Layer>
                    </Stage>

                    <PreviewImageWithGeoZone
                        open={openIMGPreview}
                        onClose={() => {
                            setOpenIMGPreview(false);
                            setHoveredFrame(null);
                        }}
                        imgSrc={image!}
                        scaleX={scaleFullCameraPTZ}
                        scaleY={scaleFullCameraPTZ}
                        geoZones={prevGeoZonesCameraPTZ}
                        cameraType={CameraType.PTZ}
                        listCameraView={cameraView}
                    />

                    {listCameraView.rows.length > 0 &&
                        hoveredFrame !== null &&
                        hoveredFrame.cameraViewId === cameraView.Id &&
                        anchorElMenuLists && (
                            <StyledMenu
                                frame={scaledPrevGeoZoneCameraPTZ[hoveredFrame.index ?? 0]}
                                frameIndex={hoveredFrame?.index ?? 0}
                                listCameraView={cameraView}
                                containerSize={containerSize}
                            />
                        )}

                    <BaseBoxDecoration
                        timeZone={timeZone}
                        date={cameraView?.Updated ?? moment().toDate()}
                        heading={
                            <Tooltip title="Refresh Overview" placement="top" arrow>
                                <Stack direction="row" justifyContent="center" sx={{}} alignItems="center">
                                    <BaseIconFrame
                                        icon={
                                            <Autorenew
                                                sx={{ fontSize: 17, transition: 'transform 0.3s ease-in-out' }}
                                                className="rotate-icon"
                                            />
                                        }
                                        onClick={refreshCameraView}
                                        style={{
                                            borderRadius: '50%',
                                            border: `1px solid ${color.white}`,
                                            cursor: 'pointer',
                                            width: 25,
                                            height: 25,
                                            ':hover': {
                                                background: 'rgba(255, 255, 255, 0.2)',
                                            },
                                            '&.MuiIconButton-root.Mui-disabled': {
                                                backgroundColor: '#eee',
                                            },
                                            '&:hover .rotate-icon': {
                                                transform: 'rotate(90deg) scale(1.1)',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Tooltip>
                        }
                    />
                </div>
            )}
        </>
    );
};
export default HistoriesPhotoCameraPTZ;
